/**
 * Pure helpers for turning a multi-repo selection (repo ids + per-repo base
 * branches) into the `repos[]` payload sent to the implementation APIs. Kept
 * free of React so the selection logic is unit-testable.
 */

export interface SelectableRepo {
  id: number
  full_name: string
  installationId: number
  default_branch?: string | null
}

export interface RepoSelectionEntry {
  repositoryName: string
  installationId: number
  baseBranch?: string
}

/**
 * Build the ordered `repos[]` payload from the selected repo ids, resolving each
 * id against the available repos and attaching its chosen base branch (if any).
 * Unknown ids are skipped; a blank base branch is omitted so the backend
 * resolves the repo's default branch.
 */
export function buildRepoSelection(
  selectedIds: string[],
  repos: SelectableRepo[],
  baseBranchById: Record<string, string | undefined>,
): RepoSelectionEntry[] {
  const entries: RepoSelectionEntry[] = []
  for (const id of selectedIds) {
    const repo = repos.find((candidate) => candidate.id.toString() === id)
    if (!repo) {
      continue
    }
    const baseBranch = baseBranchById[id]?.trim()
    entries.push({
      repositoryName: repo.full_name,
      installationId: repo.installationId,
      ...(baseBranch ? { baseBranch } : {}),
    })
  }
  return entries
}
